import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { itemTotal, reportError } from "./portalUtils";

export function useQuoteEditor(quote, onSaved) {
  // Mounted with key=quote.id: changing quotes resets state without effects wiping unsaved edits.
  const [items, setItems] = useState(() => (quote.items || []).map((item) => ({ ...item, id: item.id || crypto.randomUUID() })));
  const [taxPct, setTaxPct] = useState(quote.tax_pct ?? "");
  const [shipping, setShipping] = useState(quote.shipping ?? "");
  const [depositReceived, setDepositReceived] = useState(!!quote.deposit_received);
  const [saving, setSaving] = useState(false);
  const [approvedInfo, setApprovedInfo] = useState(null);
  useEffect(() => {
    const controller = new AbortController();
    setApprovedInfo(null);
    if (quote.email) api.get("/admin/approved-customer", { params: { email: quote.email }, signal: controller.signal })
      .then(({ data }) => { if (!controller.signal.aborted) setApprovedInfo(data); })
      .catch((error) => { if (!controller.signal.aborted) reportError(error, "Trade customer lookup"); });
    return () => controller.abort();
  }, [quote.email]);
  const update = (id, key, value) => setItems((list) => list.map((item) => item.id === id ? { ...item, [key]: value } : item));
  const remove = (id) => setItems((list) => list.filter((item) => item.id !== id));
  const add = () => setItems((list) => [...list, { id: crypto.randomUUID(), category: "Custom", panel_style: "", quantity: 1, unit_price: 0 }]);
  const subtotal = items.reduce((sum, item) => sum + itemTotal(item), 0);
  const tax = subtotal * (Number(taxPct) || 0) / 100;
  const total = subtotal + tax + (Number(shipping) || 0);
  const save = async () => {
    setSaving(true);
    try {
      const payload = items.map((item) => ({ ...item, quantity: Number(item.quantity) || 1,
        unit_price: item.unit_price === "" || item.unit_price == null ? null : Number(item.unit_price),
        discount_pct: item.discount_pct === "" || item.discount_pct == null ? null : Number(item.discount_pct) }));
      const { data } = await api.patch(`/admin/quotes/${quote.id}/items`, { items: payload,
        tax_pct: taxPct === "" ? null : Number(taxPct), shipping: shipping === "" ? null : Number(shipping), deposit_received: depositReceived });
      onSaved(data); toast.success("Quote updated.");
    } catch (error) { reportError(error, "Save quote"); }
    finally { setSaving(false); }
  };
  return { items, setItems, taxPct, setTaxPct, shipping, setShipping, depositReceived, setDepositReceived,
    saving, approvedInfo, update, remove, add, subtotal, tax, total, save };
}